import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { servicesAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, X, Plus } from "lucide-react";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function StorefrontEdit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState(null);
  const [form, setForm] = useState({ slug: "", brand_name: "", profile_image: "", bio: "", cover_image: "", gallery: [], services_offered: [] });
  const [serviceInput, setServiceInput] = useState("");
  const [slugStatus, setSlugStatus] = useState(null); // {available, reason}

  useEffect(() => {
    servicesAPI.getMyStorefront()
      .then((r) => {
        const d = r.data;
        setUserId(d.user_id);
        setForm({
          slug: d.slug || "",
          brand_name: d.brand_name || "",
          profile_image: d.profile_image || "",
          bio: d.bio || "",
          cover_image: d.cover_image || "",
          gallery: d.gallery || [],
          services_offered: d.services_offered || [],
        });
      })
      .catch(() => toast.error("Could not load storefront"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!form.slug) { setSlugStatus(null); return; }
    const t = setTimeout(() => {
      servicesAPI.checkSlug(form.slug).then((r) => setSlugStatus(r.data)).catch(() => setSlugStatus(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (file) set("cover_image", await fileToBase64(file));
  };
  const uploadProfile = async (e) => {
    const file = e.target.files?.[0];
    if (file) set("profile_image", await fileToBase64(file));
  };
  const addGallery = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - form.gallery.length);
    const b64 = await Promise.all(files.map(fileToBase64));
    set("gallery", [...form.gallery, ...b64]);
  };
  const addService = () => {
    const v = serviceInput.trim();
    if (v && !form.services_offered.includes(v)) set("services_offered", [...form.services_offered, v]);
    setServiceInput("");
  };

  const save = async () => {
    if (form.slug && slugStatus && !slugStatus.available) {
      toast.error(slugStatus.reason || "Choose a different handle");
      return;
    }
    setSaving(true);
    try {
      const res = await servicesAPI.updateMyStorefront(form);
      toast.success("Storefront saved");
      navigate(`/storefront/${res.data?.slug || userId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="mx-auto max-w-2xl py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="font-display text-3xl font-bold">Edit storefront</h1>

      <Card className="space-y-5 border-border bg-card p-5">
        <div>
          <Label htmlFor="slug">Custom storefront URL (optional)</Label>
          <div className="mt-1 flex items-center gap-1 rounded-md border border-border px-3 focus-within:border-primary">
            <span className="text-sm text-muted-foreground">/storefront/</span>
            <input
              id="slug"
              value={form.slug}
              onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              maxLength={30}
              placeholder="your-handle"
              className="flex-1 bg-transparent py-2 text-sm outline-none"
              data-testid="storefront-slug-input"
            />
          </div>
          {form.slug && slugStatus && (
            <p className={`mt-1 text-xs ${slugStatus.available ? "text-emerald-500" : "text-destructive"}`} data-testid="storefront-slug-status">
              {slugStatus.available ? "✓ Available" : slugStatus.reason}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">3–30 lowercase letters, numbers or hyphens.</p>
        </div>

        <div>
          <Label htmlFor="brand">Brand name (optional)</Label>
          <Input id="brand" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} placeholder="Leave blank to use your username" className="mt-1" data-testid="storefront-brand-input" />
        </div>
        <div>
          <Label className="mb-2 block">Storefront icon / profile image (optional)</Label>
          <div className="flex items-center gap-3">
            {form.profile_image ? (
              <div className="relative">
                <img src={form.profile_image} alt="icon" className="h-16 w-16 rounded-full object-cover" />
                <button onClick={() => set("profile_image", "")} className="absolute -right-1 -top-1 rounded-full bg-black/70 p-1 text-white"><X className="h-3 w-3" /></button>
              </div>
            ) : (
              <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border border-dashed border-border text-muted-foreground hover:border-primary">
                <ImagePlus className="h-5 w-5" />
                <input type="file" accept="image/*" className="hidden" onChange={uploadProfile} data-testid="storefront-profile-input" />
              </label>
            )}
            <p className="text-xs text-muted-foreground">Shown on your listings' "View storefront" button.</p>
          </div>
        </div>

        <div>
          <Label htmlFor="bio">Intro / bio (optional)</Label>
          <Textarea id="bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={4} placeholder="Tell customers about your experience..." className="mt-1" data-testid="storefront-bio-input" />
        </div>

        <div>
          <Label>Services you offer (optional)</Label>
          <div className="mt-1 flex gap-2">
            <Input value={serviceInput} onChange={(e) => setServiceInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addService())} placeholder="e.g. GT3 setups" data-testid="storefront-service-input" />
            <Button type="button" variant="outline" onClick={addService}><Plus className="h-4 w-4" /></Button>
          </div>
          {form.services_offered.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {form.services_offered.map((s, i) => (
                <span key={i} className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs">
                  {s}
                  <button onClick={() => set("services_offered", form.services_offered.filter((_, idx) => idx !== i))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <Label className="mb-2 block">Cover image (optional)</Label>
          {form.cover_image ? (
            <div className="relative h-32 overflow-hidden rounded-lg border border-border">
              <img src={form.cover_image} alt="cover" className="h-full w-full object-cover" />
              <button onClick={() => set("cover_image", "")} className="absolute right-2 top-2 rounded bg-black/70 p-1 text-white"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <label className="flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary">
              <ImagePlus className="h-6 w-6" /><span className="mt-1 text-xs">Upload cover</span>
              <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="storefront-cover-input" />
            </label>
          )}
        </div>

        <div>
          <Label className="mb-2 block">Gallery — previous work (optional, up to 5)</Label>
          <div className="flex flex-wrap gap-3">
            {form.gallery.map((g, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border">
                <img src={g} alt="" className="h-full w-full object-cover" />
                <button onClick={() => set("gallery", form.gallery.filter((_, idx) => idx !== i))} className="absolute right-0.5 top-0.5 rounded bg-black/70 p-0.5 text-white"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {form.gallery.length < 5 && (
              <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary">
                <ImagePlus className="h-5 w-5" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={addGallery} data-testid="storefront-gallery-input" />
              </label>
            )}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="storefront-save-button">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save storefront
        </Button>
      </Card>
    </div>
  );
}
